import React, { useState, useEffect, useRef } from 'react';
import {
  Package,
  MapPin,
  Scan,
  Plus,
  PlusSquare,
  CheckCircle,
  AlertCircle,
  Loader,
  Save,
  RotateCcw,
  Edit2,
  Trash2,
  X,
  Check,
  Search,
  ArrowRight,
  Warehouse
} from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { MediaCaptureButton } from '../../../common/media/MediaCaptureButton';
import { useAuth } from '../../../../contexts/AuthContext';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { 
  warehouseTransferService, 
  WarehouseTransferSession, 
  WarehouseTransferLine, 
  WarehouseTransferFormData,
  WarehouseTransferRequest,
  ItemLocationInfo,
  LocationItemInfo
} from '../../../../services/warehouseTransferService';
import { warehouseService } from '../../../../services/warehouseService';
import { WarehouseOption } from '../../../../types/warehouse';
import { LocationSelectionModal, ItemSelectionModal, SerialTransferModal, BatchTransferModal, ReservedStockMoveModal } from './modals';
import type { ReservedStockMoveDetails } from './modals';
import { ExtendShelfModal } from '../../shared/ExtendShelfModal';
import { ScanTextInput, NumericInput } from '../../../common/inputs';
import { BatchTransferResult } from './modals/BatchTransferModal';
import { ItemSearchModal, LocationInfo } from '../../../common/ItemSearchModal';
import { LocationSearchModal, LocationSearchResult } from '../../../common/LocationSearchModal';
import { useSimpleBarcodeScanner } from '../../../../hooks/useSimpleBarcodeScanner';
import { useBarcodeScannerEnabled } from '../../../../hooks/useBarcodeScannerEnabled';
import { MobileBarcodeCaptureInput } from '../../../common/scanning';
import { ItemsDto } from '../../../../types/items';
import { ToLocationSuggestions } from './components/ToLocationSuggestions';
import { useShowTransferToLocationSuggestions } from '../../../../hooks/useShowTransferToLocationSuggestions';
import { ApiError, translateApiError } from '../../../../utils/apiError';

interface WarehouseTransferAppProps {
  onBack: () => void;
}

interface EditingLine {
  lineID: number;
  quantity: string;
}

export const WarehouseTransferApp: React.FC<WarehouseTransferAppProps> = ({ onBack }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { currentWarehouse } = useWarehouse();
  const suggestionsEnabled = useShowTransferToLocationSuggestions();
  const [session, setSession] = useState<WarehouseTransferSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [formData, setFormData] = useState<WarehouseTransferFormData>({
    scannedCode: '',
    quantity: '',
    isScanning: false,
    validationMessage: '',
    validationType: ''
  });
  
  // State for transfer selections
  const [currentItem, setCurrentItem] = useState<{ itemNo: string; description: string; itemType?: number } | null>(null);
  const [showSerialTransferModal, setShowSerialTransferModal] = useState(false);
  const [showBatchTransferModal, setShowBatchTransferModal] = useState(false);
  // availableQuantity = ledig (saldo minus reservert), totalQuantity = saldo (TW 36427814)
  const [currentFromLocation, setCurrentFromLocation] = useState<{ locationName: string; warehouseID?: string; availableQuantity?: number; totalQuantity?: number; reservedQuantity?: number; } | null>(null);
  const [currentToLocation, setCurrentToLocation] = useState<{ locationName: string; warehouseID?: string; } | null>(null);
  const [fromWarehouse, setFromWarehouse] = useState<string>(''); // Will be set when warehouses load
  const [toWarehouse, setToWarehouse] = useState<string>(''); // Will be set when warehouses load
  
  // State for warehouse options from API
  const [warehouseOptions, setWarehouseOptions] = useState<WarehouseOption[]>([]);
  const [warehousesLoading, setWarehousesLoading] = useState(true);
  
  const [isAdding, setIsAdding] = useState(false);
  // «Flytt med reservasjon» (TW 36427814): linjen venter på operatørens bekreftelse
  const [reservedMovePrompt, setReservedMovePrompt] = useState<{ transferData: WarehouseTransferRequest; details: ReservedStockMoveDetails } | null>(null);
  const [editingLine, setEditingLine] = useState<EditingLine | null>(null);
  const [deletingLineId, setDeletingLineId] = useState<number | null>(null);
  const [updatingLineId, setUpdatingLineId] = useState<number | null>(null);
  const [filterText, setFilterText] = useState<string>('');
  
  // State for modaler
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showItemSearchModal, setShowItemSearchModal] = useState(false);
  const [showToLocationSearchModal, setShowToLocationSearchModal] = useState(false);
  const [showExtendShelfModal, setShowExtendShelfModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [availableLocations, setAvailableLocations] = useState<ItemLocationInfo[]>([]);
  const [availableItems, setAvailableItems] = useState<LocationItemInfo[]>([]);
  const [modalLocationName, setModalLocationName] = useState('');
  const [modalWarehouseID, setModalWarehouseID] = useState('');
  
  const scanInputRef = useRef<HTMLInputElement>(null);
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const editQuantityInputRef = useRef<HTMLInputElement>(null);
  const sessionInitialized = useRef(false);

  // Global barcode-capture (setting 'scanning.enableGlobalBarcodeCapture') — lar operatøren
  // skanne uten å fokusere skann-feltet. Selve hooken aktiveres lenger ned (etter handleScanInput).
  const barcodeScannerEnabled = useBarcodeScannerEnabled();

  // Når global capture er på, skal vi IKKE auto-fokusere det synlige skann-feltet — det er
  // unødvendig (MobileBarcodeCaptureInput/keydown fanger skanningen) og trigger virtuelt
  // tastatur på nettbrett. Fokus settes kun der det faktisk trengs (antallsfeltet).
  const focusScanInput = () => {
    if (!barcodeScannerEnabled) scanInputRef.current?.focus();
  };

  useEffect(() => {
    if (!sessionInitialized.current) {
      loadTransferSession();
      sessionInitialized.current = true;
    }
  }, [user?.operatorId]);
  
  // Load warehouse options
  useEffect(() => {
    loadWarehouseOptions();
  }, []);

  const loadWarehouseOptions = async () => {
    try {
      console.log('🔗 WarehouseTransferApp: Starting loadWarehouseOptions()');
      setWarehousesLoading(true);
      console.log('Loading warehouse options...');
      
      const options = await warehouseService.getWarehouseOptions();
      console.log('🔗 WarehouseTransferApp: Received options:', options);
      setWarehouseOptions(options);
      
      // Set default values when options are first loaded
      if (options.length > 0) {
        console.log('🔗 WarehouseTransferApp: Setting default warehouses');
        // Set first warehouse as default if not already set
        if (!fromWarehouse) {
          console.log('🔗 Setting fromWarehouse to:', options[0].value);
          setFromWarehouse(options[0].value);
        }
        if (!toWarehouse) {
          console.log('🔗 Setting toWarehouse to:', options[0].value);
          setToWarehouse(options[0].value);
        }
      }
      
      console.log('Loaded warehouse options:', options);
    } catch (error) {
      console.error('🚨 WarehouseTransferApp: Error loading warehouse options:', error);
      // Set fallback values if API fails
      if (!fromWarehouse) setFromWarehouse(currentWarehouse || '1');
      if (!toWarehouse) setToWarehouse(currentWarehouse || '1');
    } finally {
      console.log('🔗 WarehouseTransferApp: Finished loading, setting warehousesLoading to false');
      setWarehousesLoading(false);
    }
  };

  useEffect(() => {
    // Auto-focus på scanner input når komponenten lastes — kun når global capture er AV.
    // Med global capture på overtar MobileBarcodeCaptureInput fokus (usynlig, ingen tastatur).
    if (scanInputRef.current && !loading && !barcodeScannerEnabled) {
      scanInputRef.current.focus();
    }
  }, [loading, barcodeScannerEnabled]);

  useEffect(() => {
    // Auto-focus på edit input når editing mode starter for en ny linje
    // Bruker lineID som dependency så effekten ikke re-selekterer teksten
    // på hvert tastetrykk (quantity-endringer skaper nytt editingLine-objekt).
    if (editingLine && editQuantityInputRef.current) {
      editQuantityInputRef.current.focus();
      editQuantityInputRef.current.select();
    }
  }, [editingLine?.lineID]);

  // 🎯 NEW: Auto-focus til quantity-felt når alle nødvendige felt er fylt ut
  useEffect(() => {
    if (currentItem && currentFromLocation && currentToLocation && quantityInputRef.current) {
      // Vent litt for å sikre at UI er oppdatert før fokus settes
      setTimeout(() => {
        quantityInputRef.current?.focus();
      }, 100);
    }
  }, [currentItem, currentFromLocation, currentToLocation]);

  // Filter overføringer basert på varenummer og lokasjonskoder
  const filteredLines = session?.lines.filter((line: WarehouseTransferLine) => {
    if (!filterText.trim()) return true;
    
    const searchTerm = filterText.toLowerCase();
    const itemNo = line.itemNo.toLowerCase();
    const fromLocation = line.fromLocationName.toLowerCase();
    const toLocation = line.toLocationName.toLowerCase();
    const description = (line.description1 || '').toLowerCase();
    
    const batchNo = (line.itemBatchNo || '').toLowerCase();
    return itemNo.includes(searchTerm) ||
           fromLocation.includes(searchTerm) ||
           toLocation.includes(searchTerm) ||
           description.includes(searchTerm) ||
           batchNo.includes(searchTerm);
  }) || [];

  const loadTransferSession = async () => {
    if (!user?.operatorId || sessionLoading) return;

    setSessionLoading(true);
    setLoading(true);
    
    try {
      console.log('Loading transfer session for operator:', user.operatorId);
      
      // Søk etter eksisterende aktiv session
      const activeSession = await warehouseTransferService.getActiveTransferSession(user.operatorId);
      
      if (activeSession) {
        console.log('Found existing active transfer session:', activeSession.batchID);
        setSession(activeSession);
      } else {
        console.log('No active transfer session found, creating new one');
        // Opprett ny session første gang
        const newSession = await warehouseTransferService.createTransferSession(user.operatorId, fromWarehouse);
        console.log('Created new transfer session:', newSession.batchID);
        setSession(newSession);
      }
    } catch (error) {
      console.error(t('warehouse.modules.warehouseTransfer.errors.loadingSessionFailed'), error);
      setFormData((prev: WarehouseTransferFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.warehouseTransfer.errors.couldNotLoadSession'),
        validationType: 'error'
      }));
    } finally {
      setSessionLoading(false);
      setLoading(false);
    }
  };

  // Visningsnavn for et lager (faller tilbake til ID til listen er lastet)
  const warehouseLabelFor = (id: string | undefined): string | undefined => {
    if (!id) return undefined;
    return warehouseOptions.find(o => String(o.value) === String(id))?.label ?? id;
  };

  // Lokasjonen er sannheten: en lokasjon tilhører nøyaktig ett lager (PK CompanyNo+WarehouseID+BinCode).
  // Settes en fra-/til-lokasjon i et annet lager enn nedtrekket viser, følger nedtrekket lokasjonen.
  // Backend avviser dessuten linjer der til-lokasjon og til-lager ikke stemmer (TO_LOCATION_NOT_IN_WAREHOUSE).
  useEffect(() => {
    const binWarehouse = currentFromLocation?.warehouseID;
    if (binWarehouse && String(binWarehouse) !== String(fromWarehouse)) {
      setFromWarehouse(String(binWarehouse));
    }
  }, [currentFromLocation?.warehouseID]);

  useEffect(() => {
    const binWarehouse = currentToLocation?.warehouseID;
    if (!currentToLocation || !binWarehouse || String(binWarehouse) === String(toWarehouse)) return;
    const label = warehouseLabelFor(String(binWarehouse)) ?? String(binWarehouse);
    setToWarehouse(String(binWarehouse));
    setFormData((prev: WarehouseTransferFormData) => ({
      ...prev,
      validationMessage: t(
        'warehouse.modules.warehouseTransfer.messages.toWarehouseFollowedLocation',
        { '0': currentToLocation.locationName, '1': label },
        `Til-lokasjon satt: ${currentToLocation.locationName}. Til Lager endret til ${label} fordi lokasjonen tilhører dette lageret.`
      ),
      validationType: 'success'
    }));
  }, [currentToLocation?.locationName, currentToLocation?.warehouseID]);

  const handleScanInput = async (value: string) => {
    if (!value.trim()) return;

    console.log('🔍 [handleScanInput] Starting validation for:', value);
    const totalStartTime = performance.now();

    setFormData((prev: WarehouseTransferFormData) => ({ ...prev, isScanning: true, validationMessage: '' }));

    try {
      // Prøv først som vare
      console.log('🔍 [handleScanInput] Trying item validation...');
      const itemStartTime = performance.now();
      const itemValidation = await warehouseTransferService.validateItem(value);
      const itemEndTime = performance.now();
      console.log(`⏱️ [handleScanInput] Item validation took ${Math.round(itemEndTime - itemStartTime)}ms, valid: ${itemValidation.valid}`);
      if (itemValidation.valid) {
        // Sjekk om varen finnes på flere lokasjoner
        const locations = await warehouseTransferService.getLocationsForItem(itemValidation.itemNo, fromWarehouse || currentWarehouse, true); // TW 36386708: kun fra-lager
        
        if (locations.length === 0) {
          setFormData((prev: WarehouseTransferFormData) => ({
            ...prev,
            isScanning: false,
            validationMessage: t('warehouse.modules.warehouseTransfer.messages.itemNotFoundAtLocations', { '0': itemValidation.itemNo }),
            validationType: 'error'
          }));
          setTimeout(focusScanInput, 100);
          return;
        } else if (locations.length === 1) {
          // Kun en lokasjon - sett automatisk
          setCurrentItem({
            itemNo: itemValidation.itemNo,
            description: itemValidation.description1 || '',
            itemType: itemValidation.itemType
          });
          setCurrentFromLocation({
            locationName: locations[0].locationName,
            warehouseID: locations[0].warehouseID,
            availableQuantity: locations[0].availableQuantity,
            totalQuantity: locations[0].totalQuantity,
            reservedQuantity: locations[0].reservedQuantity
          });
          setFormData((prev: WarehouseTransferFormData) => ({
            ...prev,
            isScanning: false,
            validationMessage: t('warehouse.modules.warehouseTransfer.messages.itemAndFromLocationSet', { '0': itemValidation.itemNo, '1': locations[0].locationName }),
            validationType: 'success'
          }));
          setTimeout(focusScanInput, 100);
          return;
        } else {
          // Flere lokasjoner - vis modal
          setCurrentItem({
            itemNo: itemValidation.itemNo,
            description: itemValidation.description1 || '',
            itemType: itemValidation.itemType
          });
          setAvailableLocations(locations);
          setShowLocationModal(true);
          setFormData((prev: WarehouseTransferFormData) => ({
            ...prev,
            isScanning: false,
            validationMessage: '',
            validationType: ''
          }));
          return;
        }
      }

      // Prøv som lokasjon
      console.log('🔍 [handleScanInput] Item not valid, trying location validation...');
      const locationStartTime = performance.now();
      const locationValidation = await warehouseTransferService.validateLocation(user?.companyNo || '', value);
      const locationEndTime = performance.now();
      const totalEndTime = performance.now();
      console.log(`⏱️ [handleScanInput] Location validation took ${Math.round(locationEndTime - locationStartTime)}ms, valid: ${locationValidation.valid}`);
      console.log(`⏱️ [handleScanInput] TOTAL validation time: ${Math.round(totalEndTime - totalStartTime)}ms`);

      if (locationValidation.valid) {
        // Hvis vi ikke har en vare valgt ennå, sjekk hvilke varer som finnes på lokasjonen
        if (!currentItem) {
          const items = await warehouseTransferService.getItemsAtLocation(locationValidation.locationName, locationValidation.warehouseID || fromWarehouse || currentWarehouse);
          
          if (items.length === 0) {
            setFormData((prev: WarehouseTransferFormData) => ({
              ...prev,
              isScanning: false,
              validationMessage: t('warehouse.modules.warehouseTransfer.messages.locationHasNoAvailableItems', { '0': locationValidation.locationName }),
              validationType: 'error'
            }));
            setTimeout(focusScanInput, 100);
            return;
          } else if (items.length === 1) {
            // Kun en vare - sett automatisk
            setCurrentItem({
              itemNo: items[0].itemNo,
              description: items[0].description,
              itemType: items[0].itemType
            });
            setCurrentFromLocation({
              locationName: locationValidation.locationName,
              warehouseID: locationValidation.warehouseID,
              availableQuantity: items[0].availableQuantity,
              totalQuantity: items[0].totalQuantity,
              reservedQuantity: items[0].reservedQuantity
            });
            setFormData((prev: WarehouseTransferFormData) => ({
              ...prev,
              isScanning: false,
              validationMessage: t('warehouse.modules.warehouseTransfer.messages.itemAndFromLocationSet', { '0': items[0].itemNo, '1': locationValidation.locationName }),
              validationType: 'success'
            }));
            setTimeout(focusScanInput, 100);
            return;
          } else {
            // Flere varer - vis modal
            setAvailableItems(items);
            setModalLocationName(locationValidation.locationName);
            setModalWarehouseID(locationValidation.warehouseID || '');
            setShowItemModal(true);
            setFormData((prev: WarehouseTransferFormData) => ({
              ...prev,
              isScanning: false,
              validationMessage: '',
              validationType: ''
            }));
            return;
          }
        } else {
          // Vi har allerede en vare, så dette må være fra- eller til-lokasjon
          if (!currentFromLocation) {
            // Slå opp tilgjengelig beholdning for denne varen på lokasjonen
            let match: ItemLocationInfo | undefined;
            try {
              const itemLocations = await warehouseTransferService.getLocationsForItem(currentItem.itemNo, fromWarehouse || currentWarehouse, true);
              match = itemLocations.find(l => l.locationName === locationValidation.locationName);
            } catch (err) {
              console.warn('Kunne ikke hente tilgjengelig beholdning:', err);
            }
            setCurrentFromLocation({
              locationName: locationValidation.locationName,
              warehouseID: locationValidation.warehouseID,
              availableQuantity: match?.availableQuantity,
              totalQuantity: match?.totalQuantity,
              reservedQuantity: match?.reservedQuantity
            });
            setFormData((prev: WarehouseTransferFormData) => ({
              ...prev,
              isScanning: false,
              validationMessage: t('warehouse.modules.warehouseTransfer.messages.fromLocationSet', { '0': locationValidation.locationName }),
              validationType: 'success'
            }));
          } else if (!currentToLocation) {
            // Sjekk at til-lokasjon er forskjellig fra fra-lokasjon
            if (locationValidation.locationName === currentFromLocation.locationName) {
              setFormData((prev: WarehouseTransferFormData) => ({
                ...prev,
                isScanning: false,
                validationMessage: t('warehouse.modules.warehouseTransfer.errors.sameFromAndToLocation'),
                validationType: 'error'
              }));
              setTimeout(focusScanInput, 100);
              return;
            }
            
            setCurrentToLocation({
              locationName: locationValidation.locationName,
              warehouseID: locationValidation.warehouseID
            });
            setFormData((prev: WarehouseTransferFormData) => ({
              ...prev,
              isScanning: false,
              validationMessage: t('warehouse.modules.warehouseTransfer.messages.toLocationSet', { '0': locationValidation.locationName }),
              validationType: 'success'
            }));
          } else {
            // Begge lokasjoner er allerede satt, erstatt til-lokasjon
            if (locationValidation.locationName === currentFromLocation.locationName) {
              setFormData((prev: WarehouseTransferFormData) => ({
                ...prev,
                isScanning: false,
                validationMessage: t('warehouse.modules.warehouseTransfer.errors.sameFromAndToLocation'),
                validationType: 'error'
              }));
              setTimeout(focusScanInput, 100);
              return;
            }
            
            setCurrentToLocation({
              locationName: locationValidation.locationName,
              warehouseID: locationValidation.warehouseID
            });
            setFormData((prev: WarehouseTransferFormData) => ({
              ...prev,
              isScanning: false,
              validationMessage: t('warehouse.modules.warehouseTransfer.messages.toLocationUpdated', { '0': locationValidation.locationName }),
              validationType: 'success'
            }));
          }

          // 🎯 Fokus vil automatisk settes til quantity-feltet via useEffect når alle felt er fylt
          if (!currentItem || !currentFromLocation || !currentToLocation) {
            setTimeout(focusScanInput, 100);
          }
          return;
        }
      }

      // Ingen match funnet
      setFormData((prev: WarehouseTransferFormData) => ({
        ...prev,
        isScanning: false,
        validationMessage: t('warehouse.modules.warehouseTransfer.errors.itemOrLocationNotFound', { '0': value }),
        validationType: 'error'
      }));
      setTimeout(focusScanInput, 100);

    } catch (error) {
      console.error('Feil ved validering:', error);
      setFormData((prev: WarehouseTransferFormData) => ({
        ...prev,
        isScanning: false,
        validationMessage: t('warehouse.modules.warehouseTransfer.errors.validationError'),
        validationType: 'error'
      }));
      setTimeout(focusScanInput, 100);
    }
  };

  // Global barcode-capture: skann hvor som helst på siden uten å fokusere skann-feltet.
  // Hooken ignorerer selv tastetrykk når fokus er i et input/textarea/select-felt, så det
  // fokuserte «Skann vare og lokasjoner»-feltet håndterer skann som før (ingen dobbel-fanging).
  // Pauses mens en modal er åpen (modalen har egen skanning/innfokusert felt).
  const isAnyTransferModalOpen =
    showSerialTransferModal || showBatchTransferModal || showLocationModal ||
    showItemModal || showItemSearchModal || showToLocationSearchModal || showExtendShelfModal ||
    reservedMovePrompt !== null;

  useSimpleBarcodeScanner({
    enabled: barcodeScannerEnabled && !isAnyTransferModalOpen,
    onScan: (barcode) => handleScanInput(barcode.trim())
  });

  const handleLocationSelection = (location: ItemLocationInfo) => {
    setCurrentFromLocation({
      locationName: location.locationName,
      warehouseID: location.warehouseID,
      availableQuantity: location.availableQuantity,
      totalQuantity: location.totalQuantity,
      reservedQuantity: location.reservedQuantity
    });
    setFormData((prev: WarehouseTransferFormData) => ({
      ...prev,
      validationMessage: t('warehouse.modules.warehouseTransfer.messages.fromLocationSelected', { '0': location.locationName, '1': location.availableQuantity }),
      validationType: 'success'
    }));
    // Fokus på scanner for å skanne til-lokasjon
    setTimeout(focusScanInput, 100);
  };

  const handleItemSelection = (item: LocationItemInfo) => {
    setCurrentItem({
      itemNo: item.itemNo,
      description: item.description,
      itemType: item.itemType
    });
    setCurrentFromLocation({
      locationName: item.locationName,
      warehouseID: item.warehouseID,
      availableQuantity: item.availableQuantity,
      totalQuantity: item.totalQuantity,
      reservedQuantity: item.reservedQuantity
    });
    setFormData((prev: WarehouseTransferFormData) => ({
      ...prev,
      validationMessage: t('warehouse.modules.warehouseTransfer.messages.itemAndFromLocationSetWithQuantity', { '0': item.itemNo, '1': item.locationName, '2': item.availableQuantity }),
      validationType: 'success'
    }));
    // Fokus på scanner for å skanne til-lokasjon
    setTimeout(focusScanInput, 100);
  };

  const handleItemSearchSelect = async (item: ItemsDto, location: LocationInfo) => {
    setShowItemSearchModal(false);

    // Sett vare og fra-lokasjon basert på valgt lokasjon
    setCurrentItem({
      itemNo: item.itemNo,
      description: item.description1 || '',
      itemType: item.itemType
    });
    setCurrentFromLocation({
      locationName: location.locationName,
      warehouseID: location.warehouseID,
      availableQuantity: location.availableQuantity
    });
    setFormData((prev: WarehouseTransferFormData) => ({
      ...prev,
      validationMessage: t('warehouse.modules.warehouseTransfer.messages.itemAndFromLocationSetWithQuantity', { '0': item.itemNo, '1': location.locationName, '2': location.availableQuantity }),
      validationType: 'success'
    }));
    setTimeout(focusScanInput, 100);
  };

  const handleToLocationSelect = (location: LocationSearchResult) => {
    setShowToLocationSearchModal(false);

    // Sjekk at til-lokasjon er forskjellig fra fra-lokasjon
    if (currentFromLocation && location.binCode === currentFromLocation.locationName) {
      setFormData((prev: WarehouseTransferFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.warehouseTransfer.errors.sameFromAndToLocation'),
        validationType: 'error'
      }));
      setTimeout(focusScanInput, 100);
      return;
    }

    // Sett til-lokasjon
    setCurrentToLocation({
      locationName: location.binCode,
      warehouseID: location.warehouseID
    });
    setFormData((prev: WarehouseTransferFormData) => ({
      ...prev,
      validationMessage: t('warehouse.modules.warehouseTransfer.messages.toLocationSet', { '0': location.binCode }),
      validationType: 'success'
    }));

    // Fokus vil automatisk settes til quantity-feltet via useEffect når alle felt er fylt
    setTimeout(() => quantityInputRef.current?.focus(), 100);
  };

  // Operatøren valgte et forslag fra ToLocationSuggestions-panelet
  const handleSuggestedToLocation = (binCode: string, warehouseID: string) => {
    if (currentFromLocation && binCode === currentFromLocation.locationName) {
      setFormData((prev: WarehouseTransferFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.warehouseTransfer.errors.sameFromAndToLocation'),
        validationType: 'error'
      }));
      return;
    }

    setCurrentToLocation({ locationName: binCode, warehouseID });
    setFormData((prev: WarehouseTransferFormData) => ({
      ...prev,
      validationMessage: t('warehouse.modules.warehouseTransfer.messages.toLocationSet', { '0': binCode }),
      validationType: 'success'
    }));
    setTimeout(() => quantityInputRef.current?.focus(), 100);
  };

  // Øvre grense for antall: saldo på fra-lokasjonen (ledig når saldo ikke er kjent)
  const stockLimit = currentFromLocation?.totalQuantity ?? currentFromLocation?.availableQuantity;

  const handleAddTransfer = async () => {
    if (!session || !currentItem || !currentFromLocation || !currentToLocation) {
      setFormData((prev: WarehouseTransferFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.warehouseTransfer.errors.mustScanItemAndLocations'),
        validationType: 'error'
      }));
      return;
    }

    // Serial-varer: Vis serial-modal i stedet for å legge til med manuelt antall
    if (currentItem.itemType === 6) {
      setShowSerialTransferModal(true);
      return;
    }

    // Batch-varer: Vis batch-modal for å velge batcher og antall
    if (currentItem.itemType === 4 || currentItem.itemType === 5) {
      setShowBatchTransferModal(true);
      return;
    }

    const quantity = parseFloat(formData.quantity);
    if (isNaN(quantity) || quantity <= 0) {
      setFormData((prev: WarehouseTransferFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.warehouseTransfer.errors.mustEnterValidQuantity'),
        validationType: 'error'
      }));
      setTimeout(() => quantityInputRef.current?.focus(), 100);
      return;
    }

    // Sjekk mot beholdning på fra-lokasjon (kun for standard-varer
    // — serial/batch håndteres i egne modaler som velger fra eksisterende qty).
    // Ventende linjer i sessionen for samme vare/fra-lokasjon telles med, ellers
    // godtas f.eks. 3+3 mot saldo 3 og hele batchen blokkeres først ved fullføring.
    // Grensen er saldo: tar flyttingen av reservert saldo, spør backend om reservasjonen
    // skal flyttes med (RESERVED_STOCK_FOR_MOVE, TW 36427814).
    const available = currentFromLocation.totalQuantity ?? currentFromLocation.availableQuantity;
    const pendingInSession = session.lines
      .filter(l => l.itemNo === currentItem.itemNo && l.fromLocationName === currentFromLocation.locationName)
      .reduce((sum, l) => sum + l.quantity, 0);
    if (available !== undefined && quantity + pendingInSession > available) {
      setFormData((prev: WarehouseTransferFormData) => ({
        ...prev,
        validationMessage: pendingInSession > 0
          ? t('warehouse.modules.warehouseTransfer.errors.quantityExceedsAvailableWithPending', { '0': quantity, '1': currentFromLocation.locationName, '2': available, '3': pendingInSession }, `Antall (${quantity}) overstiger tilgjengelig beholdning på ${currentFromLocation.locationName}: saldo ${available}, hvorav ${pendingInSession} allerede er registrert i denne overføringen.`)
          : t('warehouse.modules.warehouseTransfer.errors.quantityExceedsAvailable', { '0': quantity, '1': currentFromLocation.locationName, '2': available }, `Antall (${quantity}) overstiger tilgjengelig beholdning på ${currentFromLocation.locationName} (${available}).`),
        validationType: 'error'
      }));
      setTimeout(() => quantityInputRef.current?.focus(), 100);
      return;
    }

    await submitTransferLine({
      itemNo: currentItem.itemNo,
      fromLocationName: currentFromLocation.locationName,
      toLocationName: currentToLocation.locationName,
      quantity,
      fromWarehouse,
      toWarehouse,
      description1: currentItem.description,
      itemType: currentItem.itemType
    }, false);
  };

  /**
   * Registrerer en standard-linje. Svarer backend at flyttingen tar av saldo reservert for plukk
   * (RESERVED_STOCK_FOR_MOVE), vises bekreftelsesdialogen; ved ja sendes linjen på nytt med
   * moveReservations — reservasjonene følger varen når flyttingen avsluttes (TW 36427814).
   */
  const submitTransferLine = async (transferData: WarehouseTransferRequest, moveReservations: boolean) => {
    if (!session) return;
    const quantity = transferData.quantity;
    setIsAdding(true);
    try {
      const newLine = await warehouseTransferService.addTransferLine(session.batchID, transferData, moveReservations);
      setReservedMovePrompt(null);

      // Oppdater session med ny linje
      setSession((prev: WarehouseTransferSession | null) => prev ? {
        ...prev,
        lines: [newLine, ...prev.lines],
        transactionCount: prev.transactionCount + 1
      } : null);

      // Reset form for neste overføring
      setFormData({
        scannedCode: '',
        quantity: '',
        isScanning: false,
        validationMessage: moveReservations
          ? t('warehouse.modules.warehouseTransfer.reservedMove.added', { '0': transferData.itemNo, '1': quantity, '2': transferData.fromLocationName, '3': transferData.toLocationName }, `${quantity} stk ${transferData.itemNo} flyttes fra ${transferData.fromLocationName} til ${transferData.toLocationName} — reservasjonen følger med når flyttingen avsluttes.`)
          : t('warehouse.modules.warehouseTransfer.messages.transferAdded', { '0': transferData.itemNo, '1': quantity, '2': transferData.fromLocationName, '3': transferData.toLocationName }),
        validationType: 'success'
      });
      setCurrentItem(null);
      setCurrentFromLocation(null);
      setCurrentToLocation(null);

      // 🎯 Auto-focus tilbake til scanner etter vellykket overføring
      setTimeout(focusScanInput, 100);

    } catch (error) {
      if (!moveReservations && error instanceof ApiError && error.errorCode === 'RESERVED_STOCK_FOR_MOVE' && error.details) {
        setReservedMovePrompt({ transferData, details: error.details as unknown as ReservedStockMoveDetails });
        return;
      }
      setReservedMovePrompt(null);
      console.error(t('warehouse.modules.warehouseTransfer.errors.addingTransferLineFailed'), error);
      setFormData((prev: WarehouseTransferFormData) => ({
        ...prev,
        validationMessage: translateApiError(error, t) ?? t('warehouse.modules.warehouseTransfer.errors.couldNotAddTransferLine'),
        validationType: 'error'
      }));
    } finally {
      setIsAdding(false);
    }
  };

  // Handler for serial transfer modal — oppretter én linje per valgt serienummer
  const handleSerialTransferComplete = async (selectedSerials: string[]) => {
    setShowSerialTransferModal(false);
    if (!session || !currentItem || !currentFromLocation || !currentToLocation || selectedSerials.length === 0) return;

    setIsAdding(true);
    try {
      const newLines: WarehouseTransferLine[] = [];
      for (const serial of selectedSerials) {
        const transferData: WarehouseTransferRequest = {
          itemNo: currentItem.itemNo,
          fromLocationName: currentFromLocation.locationName,
          toLocationName: currentToLocation.locationName,
          quantity: 1,
          fromWarehouse,
          toWarehouse,
          description1: currentItem.description,
          itemSerialNo: serial,
          itemType: currentItem.itemType
        };
        const newLine = await warehouseTransferService.addTransferLine(session.batchID, transferData);
        newLines.push(newLine);
      }

      // Oppdater session med alle nye linjer
      setSession((prev: WarehouseTransferSession | null) => prev ? {
        ...prev,
        lines: [...newLines, ...prev.lines],
        transactionCount: prev.transactionCount + newLines.length
      } : null);

      // Reset form
      const serialCount = selectedSerials.length;
      setFormData({
        scannedCode: '',
        quantity: '',
        isScanning: false,
        validationMessage: t('warehouse.modules.warehouseTransfer.messages.serialsAddedForTransfer', { '0': serialCount, '1': currentFromLocation.locationName, '2': currentToLocation.locationName }, `${serialCount} serienumre lagt til for overføring fra ${currentFromLocation.locationName} til ${currentToLocation.locationName}`),
        validationType: 'success'
      });
      setCurrentItem(null);
      setCurrentFromLocation(null);
      setCurrentToLocation(null);
      setTimeout(focusScanInput, 100);
    } catch (error) {
      console.error('Error adding serial transfer lines:', error);
      setFormData((prev: WarehouseTransferFormData) => ({
        ...prev,
        validationMessage: translateApiError(error, t) ?? t('warehouse.modules.warehouseTransfer.errors.couldNotAddSerialTransfers', {}, 'Kunne ikke legge til serienummer-overføringer'),
        validationType: 'error'
      }));
    } finally {
      setIsAdding(false);
    }
  };

  // Handler for batch transfer modal — oppretter én linje per valgt batch
  const handleBatchTransferComplete = async (batches: BatchTransferResult[]) => {
    setShowBatchTransferModal(false);
    if (!session || !currentItem || !currentFromLocation || !currentToLocation || batches.length === 0) return;

    setIsAdding(true);
    try {
      const newLines: WarehouseTransferLine[] = [];
      for (const batch of batches) {
        const transferData: WarehouseTransferRequest = {
          itemNo: currentItem.itemNo,
          fromLocationName: currentFromLocation.locationName,
          toLocationName: currentToLocation.locationName,
          quantity: batch.quantity,
          fromWarehouse,
          toWarehouse,
          description1: currentItem.description,
          itemBatchNo: batch.batchNo,
          itemBatchExpDate: batch.expiryDate ? new Date(batch.expiryDate.substring(0, 10) + 'T12:00:00') : undefined,
          itemType: currentItem.itemType
        };
        const newLine = await warehouseTransferService.addTransferLine(session.batchID, transferData);
        newLines.push(newLine);
      }

      setSession((prev: WarehouseTransferSession | null) => prev ? {
        ...prev,
        lines: [...newLines, ...prev.lines],
        transactionCount: prev.transactionCount + newLines.length
      } : null);

      const batchCount = batches.length;
      setFormData({
        scannedCode: '',
        quantity: '',
        isScanning: false,
        validationMessage: t('warehouse.modules.warehouseTransfer.messages.batchesAddedForTransfer', { '0': batchCount, '1': batchCount !== 1 ? 'er' : '', '2': currentFromLocation.locationName, '3': currentToLocation.locationName }, `${batchCount} batch${batchCount !== 1 ? 'er' : ''} lagt til for overføring fra ${currentFromLocation.locationName} til ${currentToLocation.locationName}`),
        validationType: 'success'
      });
      setCurrentItem(null);
      setCurrentFromLocation(null);
      setCurrentToLocation(null);
      setTimeout(focusScanInput, 100);
    } catch (error) {
      console.error('Error adding batch transfer lines:', error);
      setFormData((prev: WarehouseTransferFormData) => ({
        ...prev,
        validationMessage: translateApiError(error, t) ?? t('warehouse.modules.warehouseTransfer.errors.couldNotAddBatchTransfers', {}, 'Kunne ikke legge til batch-overføringer'),
        validationType: 'error'
      }));
    } finally {
      setIsAdding(false);
    }
  };

  const handleStartEdit = (line: WarehouseTransferLine) => {
    setEditingLine({
      lineID: line.lineID,
      quantity: line.quantity.toString()
    });
  };

  const handleCancelEdit = () => {
    setEditingLine(null);
  };

  const handleSaveEdit = async () => {
    if (!editingLine || !session) return;

    const quantity = parseFloat(editingLine.quantity);
    if (isNaN(quantity) || quantity <= 0) {
      setFormData((prev: WarehouseTransferFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.warehouseTransfer.errors.mustEnterValidQuantity'),
        validationType: 'error'
      }));
      return;
    }

    setUpdatingLineId(editingLine.lineID);
    try {
      const updatedLine = await warehouseTransferService.updateTransferLine(session.batchID, editingLine.lineID, quantity);
      
      // Oppdater session med oppdatert linje
      setSession((prev: WarehouseTransferSession | null) => prev ? {
        ...prev,
        lines: prev.lines.map(line => 
          line.lineID === editingLine.lineID ? updatedLine : line
        )
      } : null);

      setEditingLine(null);
      setFormData((prev: WarehouseTransferFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.warehouseTransfer.messages.quantityUpdated', { '0': quantity }),
        validationType: 'success'
      }));

      // Clear message after 3 seconds
      setTimeout(() => {
        setFormData((prev: WarehouseTransferFormData) => ({
          ...prev,
          validationMessage: '',
          validationType: ''
        }));
      }, 3000);

    } catch (error) {
      console.error(t('warehouse.modules.warehouseTransfer.errors.updatingTransferLineFailed'), error);
      setFormData((prev: WarehouseTransferFormData) => ({
        ...prev,
        validationMessage: translateApiError(error, t) ?? t('warehouse.modules.warehouseTransfer.errors.couldNotUpdateTransferLine'),
        validationType: 'error'
      }));
    } finally {
      setUpdatingLineId(null);
    }
  };

  const handleDeleteLine = async (line: WarehouseTransferLine) => {
    if (!session) return;

    const confirmDelete = window.confirm(
      t('warehouse.modules.warehouseTransfer.confirmations.deleteTransfer', { '0': line.itemNo, '1': line.description1, '2': line.fromLocationName, '3': line.toLocationName, '4': line.quantity })
    );
    if (!confirmDelete) return;

    setDeletingLineId(line.lineID);
    try {
      const success = await warehouseTransferService.deleteTransferLine(session.batchID, line.lineID);
      
      if (success) {
        // Fjern linje fra session
        setSession((prev: WarehouseTransferSession | null) => prev ? {
          ...prev,
          lines: prev.lines.filter(l => l.lineID !== line.lineID),
          transactionCount: prev.transactionCount - 1
        } : null);

        setFormData((prev: WarehouseTransferFormData) => ({
          ...prev,
          validationMessage: t('warehouse.modules.warehouseTransfer.messages.transferDeleted', { '0': line.itemNo }),
          validationType: 'success'
        }));

        setTimeout(() => {
          setFormData((prev: WarehouseTransferFormData) => ({
            ...prev,
            validationMessage: '',
            validationType: ''
          }));
        }, 3000);
      }

    } catch (error) {
      console.error(t('warehouse.modules.warehouseTransfer.errors.deletingTransferLineFailed'), error);
      setFormData((prev: WarehouseTransferFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.warehouseTransfer.errors.couldNotDeleteTransferLine'),
        validationType: 'error'
      }));
    } finally {
      setDeletingLineId(null);
    }
  };

  const handleReset = () => {
    setFormData({
      scannedCode: '',
      quantity: '',
      isScanning: false,
      validationMessage: '',
      validationType: ''
    });
    setCurrentItem(null);
    setCurrentFromLocation(null);
    setCurrentToLocation(null);
    setTimeout(focusScanInput, 100);
  };

  const handleCompleteSession = async () => {
    if (!session) return;

    if (session.lines.length === 0) {
      setFormData((prev: WarehouseTransferFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.warehouseTransfer.errors.cannotCompleteEmptySession'),
        validationType: 'error'
      }));
      return;
    }

    const confirmComplete = window.confirm(t('warehouse.modules.warehouseTransfer.confirmations.completeSession', { '0': session.lines.length }));
    if (!confirmComplete) return;

    try {
      const success = await warehouseTransferService.completeTransferSession(session.batchID);
      if (success) {
        // Opprett ny session og fortsett
        const newSession = await warehouseTransferService.createTransferSession(user?.operatorId || '', fromWarehouse);
        setSession(newSession);

        // Reset form
        setFormData({
          scannedCode: '',
          quantity: '',
          isScanning: false,
          validationMessage: t('warehouse.modules.warehouseTransfer.messages.sessionCompleted'),
          validationType: 'success'
        });
        setCurrentItem(null);
        setCurrentFromLocation(null);
        setCurrentToLocation(null);

        // Clear success message after 5 seconds
        setTimeout(() => {
          setFormData((prev: WarehouseTransferFormData) => ({
            ...prev,
            validationMessage: '',
            validationType: ''
          }));
        }, 5000);

        // Focus på scanner for å starte ny overføring
        setTimeout(focusScanInput, 100);
      } else {
        throw new Error(t('warehouse.modules.warehouseTransfer.errors.couldNotCompleteSession'));
      }
    } catch (error) {
      console.error(t('warehouse.modules.warehouseTransfer.errors.completingSessionFailed'), error);
      // Vis backendens konkrete årsak (f.eks. hvilken vare/lokasjon som mangler saldo)
      // i stedet for kun den generiske fullføringsfeilen
      setFormData((prev: WarehouseTransferFormData) => ({
        ...prev,
        validationMessage: translateApiError(error, t) ?? t('warehouse.modules.warehouseTransfer.errors.finalizeSessionFailed'),
        validationType: 'error'
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin h-8 w-8 text-cyan-500" />
        <span className="ml-2">{t('warehouse.modules.warehouseTransfer.messages.loadingSession')}</span>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="p-4 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-lg font-semibold mb-2">{t('warehouse.modules.warehouseTransfer.errors.couldNotLoadSession')}</h2>
        <button
          onClick={onBack}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          {t('common.buttons.backToDashboard')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobil/Enterprise-skanner (Zebra/Samsung): usynlig input som fanger skanner-data der
          global keydown (useSimpleBarcodeScanner) ikke virker på Android WebView. Gates av
          samme setting (scanning.enableGlobalBarcodeCapture) + pauses når en modal er åpen.
          Yielder fokus til det synlige skann-feltet, så de sameksisterer. */}
      <MobileBarcodeCaptureInput
        enabled={barcodeScannerEnabled && !isAnyTransferModalOpen}
        onScan={(barcode) => handleScanInput(barcode.trim())}
      />

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="px-3 sm:px-4 py-3">
          {/* Mobile: Vertical Stack, Desktop: Horizontal */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <BackButton onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg" iconSize={20} label="" />
              <div>
                <h1 className="text-base sm:text-lg font-semibold text-gray-900">{t('warehouse.modules.warehouseTransfer.title')}</h1>
                <p className="text-xs sm:text-sm text-gray-500 truncate">{session.description1}</p>
              </div>
            </div>
            <div className="flex items-center justify-end space-x-2">
              <MediaCaptureButton
                entityType="WarehouseTransfer"
                entityKey={session.batchID}
              />
              <button
                onClick={() => setShowExtendShelfModal(true)}
                className="p-2 text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors"
                title={t('warehouse.extendShelf.buttonTitle')}
                aria-label={t('warehouse.extendShelf.buttonTitle')}
              >
                <PlusSquare className="h-5 w-5" />
              </button>
              <span className="text-xs sm:text-sm bg-cyan-100 text-cyan-800 px-2 py-1 rounded whitespace-nowrap">
                {session.lines.length} {t('warehouse.modules.warehouseTransfer.labels.transfers')}
              </span>
              {session.lines.length > 0 && (
                <button
                  onClick={handleCompleteSession}
                  className="bg-green-500 text-white px-3 py-2 sm:py-1 rounded text-xs sm:text-sm hover:bg-green-600 flex items-center space-x-1 min-h-[44px] sm:min-h-0"
                >
                  <Save className="h-4 w-4" />
                  <span>{t('warehouse.modules.warehouseTransfer.actions.complete')}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-4 max-w-4xl mx-auto space-y-3 sm:space-y-4">
        {/* Toolbar */}
        <div className="bg-white rounded-lg p-3 shadow-sm border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center space-x-2">
              <Scan className="h-5 w-5 text-cyan-500" />
              <span className="font-medium text-gray-700 text-sm sm:text-base">{t('warehouse.modules.warehouseTransfer.labels.quickSelection')}</span>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <button
                onClick={() => setShowItemSearchModal(true)}
                className="flex items-center justify-center space-x-2 px-4 py-3 sm:py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm sm:text-base min-h-[44px] sm:min-h-0"
              >
                <Search className="h-4 w-4" />
                <span>{t('warehouse.modules.warehouseTransfer.actions.searchItem')}</span>
              </button>
              <button
                onClick={() => setShowToLocationSearchModal(true)}
                disabled={!currentItem || !currentFromLocation}
                className={`flex items-center justify-center space-x-2 px-4 py-3 sm:py-2 rounded-lg transition-colors text-sm sm:text-base min-h-[44px] sm:min-h-0 ${
                  currentItem && currentFromLocation
                    ? 'bg-purple-500 text-white hover:bg-purple-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                title={!currentItem || !currentFromLocation ? t('warehouse.modules.warehouseTransfer.errors.selectItemAndFromLocationFirst') : t('warehouse.modules.warehouseTransfer.actions.searchToLocation')}
              >
                <MapPin className="h-4 w-4" />
                <span>{t('warehouse.modules.warehouseTransfer.actions.searchToLocation')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Forslag til til-lokasjon (saldo auto + put-away på forespørsel) */}
        {suggestionsEnabled && currentItem && currentFromLocation && !currentToLocation && (
          <ToLocationSuggestions
            itemNo={currentItem.itemNo}
            fromLocationName={currentFromLocation.locationName}
            toWarehouse={toWarehouse}
            onSelect={handleSuggestedToLocation}
          />
        )}

        {/* Warehouse Selection */}
        <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border">
          <h2 className="font-semibold mb-3 flex items-center text-sm sm:text-base">
            <Warehouse className="h-5 w-5 mr-2 text-cyan-500" />
            {t('warehouse.modules.warehouseTransfer.labels.warehouseSettings')}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.modules.warehouseTransfer.labels.fromWarehouse')}
              </label>
              <select
                value={fromWarehouse}
                onChange={(e) => {
                  const next = e.target.value;
                  setFromWarehouse(next);
                  // Fra-lokasjonen tilhører ett bestemt lager — velges et annet lager manuelt, nullstilles den
                  if (currentFromLocation?.warehouseID && String(currentFromLocation.warehouseID) !== next) {
                    setCurrentFromLocation(null);
                  }
                }}
                className="w-full p-3 sm:p-2 border border-gray-300 rounded focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-base sm:text-sm min-h-[44px] sm:min-h-0"
                disabled={warehousesLoading}
              >
                {warehousesLoading ? (
                  <option>{t('warehouse.modules.warehouseTransfer.messages.loadingWarehouses')}</option>
                ) : warehouseOptions.length > 0 ? (
                  warehouseOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="1">{t('warehouse.modules.warehouseTransfer.warehouse.options.main')}</option>
                    <option value="2">{t('warehouse.modules.warehouseTransfer.warehouse.options.reserve')}</option>
                    <option value="3">{t('warehouse.modules.warehouseTransfer.warehouse.options.return')}</option>
                  </>
                )}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.modules.warehouseTransfer.labels.toWarehouse')}
              </label>
              <select
                value={toWarehouse}
                onChange={(e) => {
                  const next = e.target.value;
                  setToWarehouse(next);
                  // Til-lokasjonen tilhører ett bestemt lager — velges et annet lager manuelt, nullstilles den
                  if (currentToLocation?.warehouseID && String(currentToLocation.warehouseID) !== next) {
                    setCurrentToLocation(null);
                  }
                }}
                className="w-full p-3 sm:p-2 border border-gray-300 rounded focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-base sm:text-sm min-h-[44px] sm:min-h-0"
                disabled={warehousesLoading}
              >
                {warehousesLoading ? (
                  <option>{t('warehouse.modules.warehouseTransfer.messages.loadingWarehouses')}</option>
                ) : warehouseOptions.length > 0 ? (
                  warehouseOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="1">{t('warehouse.modules.warehouseTransfer.warehouse.options.main')}</option>
                    <option value="2">{t('warehouse.modules.warehouseTransfer.warehouse.options.reserve')}</option>
                    <option value="3">{t('warehouse.modules.warehouseTransfer.warehouse.options.return')}</option>
                  </>
                )}
              </select>
            </div>
          </div>
        </div>

        {/* Scanning og Input sektor */}
        <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border">
          <h2 className="font-semibold mb-3 flex items-center text-sm sm:text-base">
            <Scan className="h-5 w-5 mr-2 text-cyan-500" />
            {t('warehouse.modules.warehouseTransfer.labels.scanAndInput')}
          </h2>

          <div className="space-y-3">
            {/* Scanner input */}
            <div className="relative">
              <ScanTextInput
                ref={scanInputRef}
                value={formData.scannedCode}
                onChange={(e) => setFormData((prev: WarehouseTransferFormData) => ({ ...prev, scannedCode: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && formData.scannedCode.trim()) {
                    handleScanInput(formData.scannedCode.trim());
                    setFormData((prev: WarehouseTransferFormData) => ({ ...prev, scannedCode: '' }));
                  } else if (e.key === 'Tab' && currentItem && currentFromLocation && currentToLocation) {
                    // If all fields are set, TAB should go to quantity input
                    e.preventDefault();
                    quantityInputRef.current?.focus();
                  }
                }}
                placeholder={t('warehouse.modules.warehouseTransfer.placeholders.scanItemOrLocation')}
                className="w-full p-3 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-base min-h-[44px]"
                disabled={formData.isScanning || showLocationModal || showItemModal}
                tabIndex={1}
                autoFocus
              />
              {formData.isScanning && (
                <div className="absolute right-3 top-3">
                  <Loader className="animate-spin h-5 w-5 text-cyan-500" />
                </div>
              )}
            </div>

            {/* Validation message */}
            {formData.validationMessage && (
              <div className={`p-3 sm:p-2 rounded flex items-start sm:items-center text-sm ${
                formData.validationType === 'success'
                  ? 'bg-green-50 text-green-800'
                  : 'bg-red-50 text-red-800'
              }`}>
                {formData.validationType === 'success' ? (
                  <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5 sm:mt-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5 sm:mt-0" />
                )}
                <span className="break-words">{formData.validationMessage}</span>
              </div>
            )}

            {/* Current selections */}
            <div className="space-y-3">
              {/* Current Item */}
              <div className={`p-3 border rounded-lg ${currentItem ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center">
                  <Package className={`h-4 w-4 mr-2 flex-shrink-0 ${currentItem ? 'text-green-600' : 'text-gray-400'}`} />
                  <span className="font-medium text-sm">{t('warehouse.modules.warehouseTransfer.labels.item')}</span>
                </div>
                {currentItem ? (
                  <div className="mt-1">
                    <div className="flex items-center gap-2">
                      <div className="font-mono text-sm break-all">{currentItem.itemNo}</div>
                      {currentItem.itemType === 6 && (
                        <span className="px-1.5 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full">Serial</span>
                      )}
                      {(currentItem.itemType === 4 || currentItem.itemType === 5) && (
                        <span className="px-1.5 py-0.5 bg-orange-100 text-orange-800 text-xs rounded-full">Batch</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 break-words">{currentItem.description}</div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 mt-1">{t('warehouse.modules.warehouseTransfer.labels.notScanned')}</div>
                )}
              </div>

              {/* Transfer Locations - Stack vertically on mobile */}
              <div className="grid grid-cols-1 gap-3">
                {/* From Location */}
                <div className={`p-3 border rounded-lg ${currentFromLocation ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center">
                    <MapPin className={`h-4 w-4 mr-2 flex-shrink-0 ${currentFromLocation ? 'text-blue-600' : 'text-gray-400'}`} />
                    <span className="font-medium text-sm">{t('warehouse.modules.warehouseTransfer.labels.fromLocation')}</span>
                  </div>
                  {currentFromLocation ? (
                    <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                      <span className="font-mono text-sm break-all">{currentFromLocation.locationName}</span>
                      {(currentFromLocation.totalQuantity ?? currentFromLocation.availableQuantity) !== undefined && (
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {t('warehouse.modules.warehouseTransfer.labels.onStock', { '0': currentFromLocation.totalQuantity ?? currentFromLocation.availableQuantity }, `På lager: ${currentFromLocation.totalQuantity ?? currentFromLocation.availableQuantity}`)}
                        </span>
                      )}
                      {(currentFromLocation.reservedQuantity ?? 0) > 0 && (
                        <span className="text-xs text-amber-700 whitespace-nowrap">
                          {t('warehouse.modules.warehouseTransfer.reservedMove.reservedOnLocation', { '0': currentFromLocation.reservedQuantity }, `Reservert: ${currentFromLocation.reservedQuantity}`)}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 mt-1">{t('warehouse.modules.warehouseTransfer.labels.notScanned')}</div>
                  )}
                </div>

                {/* Arrow indicator on mobile */}
                {currentFromLocation && (
                  <div className="flex justify-center -my-2">
                    <div className="bg-white rounded-full p-2 border border-gray-200 shadow-sm">
                      <ArrowRight className="h-4 w-4 text-gray-400 transform rotate-90" />
                    </div>
                  </div>
                )}

                {/* To Location */}
                <div className={`p-3 border rounded-lg ${currentToLocation ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center">
                    <MapPin className={`h-4 w-4 mr-2 flex-shrink-0 ${currentToLocation ? 'text-purple-600' : 'text-gray-400'}`} />
                    <span className="font-medium text-sm">{t('warehouse.modules.warehouseTransfer.labels.toLocation')}</span>
                  </div>
                  {currentToLocation ? (
                    <div className="mt-1">
                      <div className="font-mono text-sm break-all">{currentToLocation.locationName}</div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 mt-1">{t('warehouse.modules.warehouseTransfer.labels.notScanned')}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Quantity input og buttons - kun vis når alt er skannet */}
            {currentItem && currentFromLocation && currentToLocation && (
              <div className="space-y-3">
                {currentItem.itemType === 6 ? (
                  /* Serial-vare: Vis "Velg serienumre"-knapp i stedet for kvantitets-felt */
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-purple-600 font-medium text-sm">{t('warehouse.modules.warehouseTransfer.labels.serialItem', {}, 'Serienummervare')}</span>
                      <span className="px-1.5 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full">{t('warehouse.modules.warehouseTransfer.labels.typeLabel', { '0': currentItem.itemType }, `Type ${currentItem.itemType}`)}</span>
                    </div>
                    <p className="text-xs text-purple-700 mb-3">{t('warehouse.modules.warehouseTransfer.messages.selectSerialsToMove', { '0': currentFromLocation.locationName, '1': currentToLocation.locationName }, `Velg hvilke serienumre som skal flyttes fra ${currentFromLocation.locationName} til ${currentToLocation.locationName}`)}</p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleAddTransfer}
                        disabled={isAdding}
                        className={`flex-1 px-4 py-3 sm:py-2 rounded font-medium flex items-center justify-center space-x-2 min-h-[44px] sm:min-h-0 text-sm sm:text-base ${
                          !isAdding
                            ? 'bg-purple-600 text-white hover:bg-purple-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {isAdding ? (
                          <Loader className="animate-spin h-4 w-4" />
                        ) : (
                          <Package className="h-4 w-4" />
                        )}
                        <span>{t('warehouse.modules.warehouseTransfer.actions.selectSerials', {}, 'Velg serienumre')}</span>
                      </button>
                      <button
                        onClick={handleReset}
                        className="p-3 sm:p-2 border border-gray-300 rounded hover:bg-gray-50 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
                        title={t('warehouse.modules.warehouseTransfer.actions.reset')}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : (currentItem.itemType === 4 || currentItem.itemType === 5) ? (
                  /* Batch-vare: Vis "Velg batcher"-knapp med orange tema */
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-orange-600 font-medium text-sm">{t('warehouse.modules.warehouseTransfer.labels.batchItem', {}, 'Batchvare')}</span>
                      <span className="px-1.5 py-0.5 bg-orange-100 text-orange-800 text-xs rounded-full">{t('warehouse.modules.warehouseTransfer.labels.typeLabel', { '0': currentItem.itemType }, `Type ${currentItem.itemType}`)}</span>
                    </div>
                    <p className="text-xs text-orange-700 mb-3">{t('warehouse.modules.warehouseTransfer.messages.selectBatchesToMove', { '0': currentFromLocation.locationName, '1': currentToLocation.locationName }, `Velg hvilke batcher som skal flyttes fra ${currentFromLocation.locationName} til ${currentToLocation.locationName}`)}</p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleAddTransfer}
                        disabled={isAdding}
                        className={`flex-1 px-4 py-3 sm:py-2 rounded font-medium flex items-center justify-center space-x-2 min-h-[44px] sm:min-h-0 text-sm sm:text-base ${
                          !isAdding
                            ? 'bg-orange-600 text-white hover:bg-orange-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {isAdding ? (
                          <Loader className="animate-spin h-4 w-4" />
                        ) : (
                          <Package className="h-4 w-4" />
                        )}
                        <span>{t('warehouse.modules.warehouseTransfer.actions.selectBatches', {}, 'Velg batcher')}</span>
                      </button>
                      <button
                        onClick={handleReset}
                        className="p-3 sm:p-2 border border-gray-300 rounded hover:bg-gray-50 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
                        title={t('warehouse.modules.warehouseTransfer.actions.reset')}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Standard-vare: Kvantitets-felt og legg til-knapp */
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('warehouse.modules.warehouseTransfer.labels.quantityToTransfer')}
                      </label>
                      <NumericInput
                        ref={quantityInputRef}
                        mode="decimal"
                        value={formData.quantity}
                        onChange={(e) => setFormData((prev: WarehouseTransferFormData) => ({ ...prev, quantity: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && currentItem && currentFromLocation && currentToLocation && formData.quantity.trim()) {
                            handleAddTransfer();
                          }
                        }}
                        placeholder={t('warehouse.modules.warehouseTransfer.placeholders.enterQuantity')}
                        className={`w-full p-3 sm:p-2 border rounded focus:ring-2 focus:border-transparent text-base min-h-[44px] sm:min-h-0 ${
                          formData.quantity.trim() &&
                          stockLimit !== undefined &&
                          parseFloat(formData.quantity) > stockLimit
                            ? 'border-red-500 focus:ring-red-500 bg-red-50'
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                        min="0.01"
                        max={stockLimit}
                        tabIndex={2}
                      />
                      {currentFromLocation?.availableQuantity !== undefined && (
                        <p className={`text-xs mt-1 ${
                          formData.quantity.trim() &&
                          stockLimit !== undefined &&
                          parseFloat(formData.quantity) > stockLimit
                            ? 'text-red-600 font-medium'
                            : 'text-gray-500'
                        }`}>
                          {t('warehouse.modules.warehouseTransfer.labels.availableAtLocation', { '0': currentFromLocation.locationName, '1': currentFromLocation.availableQuantity }, `Tilgjengelig på ${currentFromLocation.locationName}: ${currentFromLocation.availableQuantity}`)}
                        </p>
                      )}
                      {/* Over ledig, innenfor saldo: reservert vare kan flyttes med reservasjonen (TW 36427814) */}
                      {formData.quantity.trim() &&
                        currentFromLocation?.availableQuantity !== undefined &&
                        stockLimit !== undefined &&
                        parseFloat(formData.quantity) > currentFromLocation.availableQuantity &&
                        parseFloat(formData.quantity) <= stockLimit && (
                        <p className="text-xs mt-1 text-amber-700">
                          {t('warehouse.modules.warehouseTransfer.reservedMove.overFreeHint', {}, 'Mer enn ledig antall — du får spørsmål om å flytte reservasjonen med.')}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleAddTransfer}
                        disabled={!currentItem || !currentFromLocation || !currentToLocation || !formData.quantity.trim() || isAdding}
                        className={`flex-1 px-4 py-3 sm:py-2 rounded font-medium flex items-center justify-center space-x-2 min-h-[44px] sm:min-h-0 text-sm sm:text-base ${
                          currentItem && currentFromLocation && currentToLocation && formData.quantity.trim() && !isAdding
                            ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {isAdding ? (
                          <Loader className="animate-spin h-4 w-4" />
                        ) : (
                          <Plus className="h-4 w-4" />
                        )}
                        <span>{t('warehouse.modules.warehouseTransfer.actions.transfer')}</span>
                      </button>

                      <button
                        onClick={handleReset}
                        className="p-3 sm:p-2 border border-gray-300 rounded hover:bg-gray-50 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
                        title={t('warehouse.modules.warehouseTransfer.actions.reset')}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Overførings liste */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-3 sm:p-4 border-b">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <h2 className="font-semibold flex items-center text-sm sm:text-base">
                <CheckCircle className="h-5 w-5 mr-2 text-green-500 flex-shrink-0" />
                <span>{t('warehouse.modules.warehouseTransfer.labels.transfers')} ({session.lines.length})</span>
                {filterText && (
                  <span className="ml-2 text-sm text-gray-500">
                    - {t('warehouse.modules.warehouseTransfer.labels.showing')} {filteredLines.length}
                  </span>
                )}
              </h2>
            </div>

            {/* Filter input */}
            <div className="relative">
              <input
                type="text"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder={t('warehouse.modules.warehouseTransfer.placeholders.filterTransfers')}
                className="w-full pl-10 pr-10 py-3 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-base sm:text-sm min-h-[44px] sm:min-h-0"
                tabIndex={10}
              />
              <Search className="absolute left-3 top-3.5 sm:top-2.5 h-4 w-4 text-gray-400" />
              {filterText && (
                <button
                  onClick={() => setFilterText('')}
                  className="absolute right-3 top-3 sm:top-2 p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          
          {session.lines.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm sm:text-base">{t('warehouse.modules.warehouseTransfer.messages.noTransfersYet')}</p>
              <p className="text-xs sm:text-sm">{t('warehouse.modules.warehouseTransfer.messages.scanToStart')}</p>
            </div>
          ) : filteredLines.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Search className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm sm:text-base">{t('warehouse.modules.warehouseTransfer.messages.noTransfersMatchedFilter')}</p>
              <p className="text-xs sm:text-sm">{t('warehouse.modules.warehouseTransfer.messages.tryDifferentSearchOrRemoveFilter')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {filteredLines.map((line: WarehouseTransferLine) => (
                <div key={line.lineID} className="p-3 sm:p-4 hover:bg-gray-50">
                  <div className="flex flex-col gap-3">
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                        <div className="font-mono text-sm font-semibold break-all">
                          {line.itemNo}
                        </div>
                        <div className="text-sm text-gray-600 break-words">
                          {line.description1}
                        </div>
                        {line.itemSerialNo && (
                          <span className="px-1.5 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full font-mono">
                            SN: {line.itemSerialNo}
                          </span>
                        )}
                        {line.itemBatchNo && (
                          <span className="px-1.5 py-0.5 bg-orange-100 text-orange-800 text-xs rounded-full font-mono">
                            {line.itemBatchNo}
                            {line.itemBatchExpDate && (
                              <span className="text-orange-600 ml-1">({new Date(line.itemBatchExpDate).toLocaleDateString('no-NO')})</span>
                            )}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-4 mt-2 text-xs sm:text-sm text-gray-500">
                        <span className="flex items-center">
                          <MapPin className="h-3 w-3 mr-1 text-blue-500 flex-shrink-0" />
                          <span className="break-all">{line.fromLocationName}</span>
                        </span>
                        <ArrowRight className="h-3 w-3 text-gray-400 hidden sm:block" />
                        <span className="flex items-center">
                          <MapPin className="h-3 w-3 mr-1 text-purple-500 flex-shrink-0" />
                          <span className="break-all">{line.toLocationName}</span>
                        </span>

                        {/* Quantity - editable or display */}
                        {editingLine && editingLine.lineID === line.lineID ? (
                          <div className="flex items-center gap-2">
                            <span>{t('warehouse.modules.warehouseTransfer.labels.quantity')}:</span>
                            <NumericInput
                              ref={editQuantityInputRef}
                              mode="decimal"
                              value={editingLine.quantity}
                              onChange={(e) => setEditingLine(prev => prev ? { ...prev, quantity: e.target.value } : null)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveEdit();
                                } else if (e.key === 'Escape') {
                                  handleCancelEdit();
                                }
                              }}
                              className="w-20 px-2 py-1 border border-cyan-300 rounded text-sm focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                              min="0.01"
                            />
                            <div className="flex items-center gap-1">
                              <button
                                onClick={handleSaveEdit}
                                disabled={updatingLineId === line.lineID}
                                className="p-2 text-green-600 hover:bg-green-100 rounded min-h-[32px] min-w-[32px] flex items-center justify-center"
                                title={t('warehouse.modules.warehouseTransfer.actions.save')}
                              >
                                {updatingLineId === line.lineID ? (
                                  <Loader className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Check className="h-3 w-3" />
                                )}
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded min-h-[32px] min-w-[32px] flex items-center justify-center"
                                title={t('common.buttons.cancel')}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span>{t('warehouse.modules.warehouseTransfer.labels.quantity')}: {line.quantity}</span>
                        )}

                        <span className="whitespace-nowrap">{new Date(line.transDt).toLocaleTimeString('no-NO')}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between sm:justify-start gap-2">
                      <span className="bg-cyan-100 text-cyan-800 px-2 py-1 rounded text-xs sm:text-sm whitespace-nowrap">
                        {line.statusText}
                      </span>

                      {/* Action buttons - kun vis hvis ikke i edit mode */}
                      {!editingLine && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleStartEdit(line)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
                            title={t('warehouse.modules.warehouseTransfer.actions.editQuantity')}
                          >
                            <Edit2 className="h-4 w-4 sm:h-3 sm:w-3" />
                          </button>
                          <button
                            onClick={() => handleDeleteLine(line)}
                            disabled={deletingLineId === line.lineID}
                            className="p-2 text-red-600 hover:bg-red-100 rounded min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
                            title={t('warehouse.modules.warehouseTransfer.actions.deleteTransfer')}
                          >
                            {deletingLineId === line.lineID ? (
                              <Loader className="h-4 w-4 sm:h-3 sm:w-3 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4 sm:h-3 sm:w-3" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modaler */}
      {reservedMovePrompt && (
        <ReservedStockMoveModal
          details={reservedMovePrompt.details}
          busy={isAdding}
          onConfirm={() => submitTransferLine(reservedMovePrompt.transferData, true)}
          onCancel={() => {
            setReservedMovePrompt(null);
            setTimeout(() => quantityInputRef.current?.focus(), 100);
          }}
        />
      )}

      <LocationSelectionModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        onSelect={handleLocationSelection}
        itemNo={currentItem?.itemNo || ''}
        itemDescription={currentItem?.description || ''}
        locations={availableLocations}
        loading={modalLoading}
      />

      <ItemSelectionModal
        isOpen={showItemModal}
        onClose={() => setShowItemModal(false)}
        onSelect={handleItemSelection}
        locationName={modalLocationName}
        warehouseID={modalWarehouseID}
        items={availableItems}
        loading={modalLoading}
      />

      <ItemSearchModal
        isOpen={showItemSearchModal}
        onClose={() => setShowItemSearchModal(false)}
        onSelect={handleItemSearchSelect}
        title={t('warehouse.modules.warehouseTransfer.modals.searchItemTitle')}
        warehouseId={fromWarehouse || currentWarehouse}
        fetchLocations={async (itemNo: string) => {
          const locations = await warehouseTransferService.getLocationsForItem(itemNo, fromWarehouse || currentWarehouse);
          return locations.map(loc => ({
            locationName: loc.locationName,
            warehouseID: loc.warehouseID || '',
            availableQuantity: loc.availableQuantity
          }));
        }}
      />

      <LocationSearchModal
        isOpen={showToLocationSearchModal}
        onClose={() => setShowToLocationSearchModal(false)}
        onSelect={handleToLocationSelect}
        title={t('warehouse.modules.warehouseTransfer.modals.searchToLocationTitle')}
        excludeLocation={currentFromLocation?.locationName}
        warehouseID={toWarehouse || undefined}
        warehouseLabel={warehouseLabelFor(toWarehouse)}
      />

      {showSerialTransferModal && currentItem && currentFromLocation && (
        <SerialTransferModal
          isOpen={showSerialTransferModal}
          onClose={() => setShowSerialTransferModal(false)}
          onComplete={handleSerialTransferComplete}
          itemNo={currentItem.itemNo}
          itemDescription={currentItem.description}
          binCode={currentFromLocation.locationName}
          warehouseId={currentFromLocation.warehouseID || fromWarehouse}
        />
      )}

      {showBatchTransferModal && currentItem && currentFromLocation && (
        <BatchTransferModal
          isOpen={showBatchTransferModal}
          onClose={() => setShowBatchTransferModal(false)}
          onComplete={handleBatchTransferComplete}
          itemNo={currentItem.itemNo}
          itemDescription={currentItem.description}
          binCode={currentFromLocation.locationName}
          warehouseId={currentFromLocation.warehouseID || fromWarehouse}
        />
      )}

      {/* Extend Shelf Modal — utvid hylle med neste lokasjon.
          Blank prefill: operatør scanner/skriver parent-BinCode.
          Ved opprettelse: auto-velg ny bin som til-lokasjon (destinasjon). */}
      <ExtendShelfModal
        isOpen={showExtendShelfModal}
        onClose={() => setShowExtendShelfModal(false)}
        initialParentBinCode=""
        onCreated={(newBinCode) => {
          // Auto-velg ny bin som til-lokasjon. Operatør har tydelig uttrykt intent om å bruke den.
          // Behold warehouseID fra eksisterende til/fra-lokasjon hvis tilgjengelig.
          const inheritedWarehouseID =
            currentToLocation?.warehouseID
            || currentFromLocation?.warehouseID
            || toWarehouse
            || fromWarehouse;
          setCurrentToLocation({
            locationName: newBinCode,
            warehouseID: inheritedWarehouseID,
          });
          setShowExtendShelfModal(false);
        }}
      />
    </div>
  );
};
